import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import Preferences from "../../components/common/card/Preferences";
import { addToCart, fetchCartInfo } from "../../store/thunk/cartThunk";
import { selectUser } from "../../store/reducer/authSlice";
import { addLocalToCart } from "../../store/reducer/cartSlice";
import useDeviceType from "../../hooks/useDeviceType";

const SubscriptionModal = ({
  open,
  setOpen,
  product,
  onSubscribe,
  productPreferences,
  isUpdateMode = false,
  subscriptionData = null,
}) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const { coreSettings } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const { isMobile } = useDeviceType();

  const [frequency, setFrequency] = useState("weekly");
  const [selectedPreference, setSelectedPreference] = useState([]);
  const [selectedCut, setSelectedCut] = useState(null);
  const [customNotes, setCustomNotes] = useState("");
  const [preferenceTotal, setPreferenceTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Get frequency options from settings API
  const frequencyOptions = useMemo(() => {
    if (coreSettings?.SUBSCRIPTION_FREQUENCIES?.length > 0) {
      return coreSettings.SUBSCRIPTION_FREQUENCIES.filter(
        (freq) => freq.status === 1
      ) // Only active frequencies
        .map((freq) => ({
          id: freq.id,
          value: freq.short_name,
          label: freq.name,
          days: freq.days,
          description: freq.description,
        }));
    }
    // Fallback to default options if API data not available
    return [
      { value: "weekly", label: "Weekly", days: 7 },
      { value: "biweekly", label: "Bi-Weekly", days: 14 },
      { value: "monthly", label: "Monthly", days: 30 },
    ];
  }, [coreSettings]);

  // Calculate preference total
  const calculatePreferenceTotal = useCallback(
    (preferences = [], cut = null) => {
      let total = 0;

      if (preferences && preferences.length > 0) {
        total += preferences.reduce((sum, pref) => sum + (pref.price || 0), 0);
      }

      if (cut && cut.price) {
        total += cut.price;
      }

      return parseFloat(total.toFixed(2));
    },
    []
  );

  // Handle preference updates from child component (similar to MobileBottomSheet)
  const handlePreferenceUpdate = useCallback(
    (data) => {
      const { preferences = [], cut = null, custom_notes = "" } = data;

      // Update state but don't save yet
      setSelectedPreference(preferences);
      setSelectedCut(cut);
      setCustomNotes(custom_notes);

      // Calculate new total
      const newTotal = calculatePreferenceTotal(preferences, cut);
      setPreferenceTotal(newTotal);
    },
    [calculatePreferenceTotal]
  );

  // Calculate total price per delivery
  const totalPricePerDelivery = useMemo(() => {
    const basePrice = parseFloat(product?.product_price || product?.price || 0);
    return (basePrice + preferenceTotal).toFixed(2);
  }, [product, preferenceTotal]);

  const handleSubscribe = async () => {
    if (isUpdateMode) {
      if (!onSubscribe) return;
      
      const selectedOption = frequencyOptions.find(f => f.value === frequency);
      const oldFrequencyLabel = subscriptionData?.frequency_details?.description || "Previous";
      const newFrequencyLabel = selectedOption?.label || frequency;
      
      const updateData = {
        frequency_id: selectedOption?.id,
        update_reason: `Changed my frequency from ${oldFrequencyLabel} to ${newFrequencyLabel}`
      };
      
      setLoading(true);
      try {
        await onSubscribe(updateData);
        // Form reset handled externally or upon success
      } finally {
        setLoading(false);
      }
      return;
    }

    // If user is not logged in, add to local cart
    if (!user) {
      const subscriptionPreferences = [];
      if (selectedPreference && selectedPreference.length > 0) {
        selectedPreference.forEach((pref) => {
          subscriptionPreferences.push({
            label: pref.name,
            price: pref.price || 0,
          });
        });
      }

      let subscriptionCuts = null;
      if (selectedCut) {
        subscriptionCuts = {
          label: selectedCut.name,
          price: selectedCut.price || 0,
        };
      }

      const subscriptionDetails = {
        enable_subscription: true,
        subscription_frequency: frequency,
        subscription_preferences: subscriptionPreferences,
        subscription_cuts: subscriptionCuts,
        subscription_custom_notes: customNotes || "",
      };

      // Create a product object that includes subscription details
      const productWithSubscription = {
        ...product,
        ...subscriptionDetails,
        cart_preference: {
          preference: subscriptionPreferences,
          cut: subscriptionCuts,
          custom_note: customNotes,
        },
      };

      dispatch(
        addLocalToCart({
          product: productWithSubscription,
          quantity: parseInt(product?.minimum_quantity) || 1,
        })
      );

      if (onSubscribe) {
        onSubscribe({ ...subscriptionDetails, isSession: true });
      }

      setOpen(false);
      navigate(isMobile ? "/buy" : "/checkout");
      return;
    }

    setLoading(true);
    try {
      // Build subscription preferences array
      const subscriptionPreferences = [];
      if (selectedPreference && selectedPreference.length > 0) {
        selectedPreference.forEach((pref) => {
          subscriptionPreferences.push({
            label: pref.name,
            price: pref.price || 0,
          });
        });
      }

      // Build subscription cuts object
      let subscriptionCuts = null;
      if (selectedCut) {
        subscriptionCuts = {
          label: selectedCut.name,
          price: selectedCut.price || 0,
        };
      }

      // Build payload for add-to-cart API
      const payload = {
        product_id: product.product_id,
        product_quantity: parseInt(product?.minimum_quantity) || 1,
        product_type: "default",
        order_sub_type_id: "",
        enable_subscription: true,
        subscription_frequency: frequency,
        subscription_preferences: subscriptionPreferences,
        subscription_cuts: subscriptionCuts,
        subscription_custom_notes: customNotes || "",
      };
      // Call add-to-cart API
      const response = await dispatch(addToCart(payload)).unwrap();

      if (response.status === 1) {
        await dispatch(fetchCartInfo());

        // Call parent onSubscribe callback if provided
        if (onSubscribe) {
          await onSubscribe({
            product_id: product.product_id,
            frequency: frequency,
            subscription_preferences: subscriptionPreferences,
            subscription_cuts: subscriptionCuts,
            custom_notes: customNotes,
          });
        }

        setOpen(false);

        // Reset form
        setFrequency("weekly");
        setSelectedPreference([]);
        setSelectedCut(null);
        setCustomNotes("");
        setPreferenceTotal(0);

        // Navigate to order summary/buy or checkout (same as Buy Now)
        if (user.order_count > 0) {
          navigate(isMobile ? "/buy" : "/order-summary");
        } else {
          navigate(isMobile ? "/buy" : "/checkout");
        }
      } else {
        toast.error(response.message || "Failed to add subscription");
      }
    } catch (error) {
      console.error("Subscription error:", error);
      toast.error(error || "Failed to add subscription. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-500/75 transition-opacity" />
      <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel className="relative transform rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl max-h-[90vh] flex flex-col">
            {/* Header with Gradient Background */}
            <div className="bg-gradient1 px-6 py-4 relative flex-shrink-0 rounded-t-lg">
              <h3 className="text-xl font-semibold text-white">
                {isUpdateMode ? "Upgrade Subscription Frequency" : "Subscribe & Save"}
              </h3>
              {/* Close Button */}
              <button
                onClick={() => setOpen(false)}
                className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Form Content - Scrollable */}
            <div className="px-6 py-3 overflow-y-auto flex-1">
              <div className="">
                {/* Frequency Selection */}
                <div className="mb-2">
                    <h2 className="text-primary-text text-lg font-semibold pb-3">{product?.product_name || product?.name}</h2>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Delivery Frequency
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {frequencyOptions.map((option) => (
                      <label
                        key={option.value}
                        className={`relative flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          frequency === option.value
                            ? "border-primary-100 bg-red-50"
                            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center flex-1">
                          <input
                            type="radio"
                            name="frequency"
                            value={option.value}
                            checked={frequency === option.value}
                            onChange={(e) => setFrequency(e.target.value)}
                            className="w-4 h-4 text-primary-100 border-gray-300"
                          />
                          <span
                            className={`ml-3 text-sm font-medium ${
                              frequency === option.value
                                ? "text-primary-100"
                                : "text-gray-700"
                            }`}
                          >
                            {option.label}
                          </span>
                        </div>
                        {frequency === option.value && (
                          <svg
                            className="w-5 h-5 text-primary-100 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Preferences Section */}
                {productPreferences && (
                  <div className="">
                    <Preferences
                      customNotesView="block"
                      bottomSheet={true}
                      page="home"
                      productId={product.product_id}
                      currentQty={0}
                      productPreferences={productPreferences}
                      cartPreference={{}}
                      cartProductNotes=""
                      onPreferenceUpdate={handlePreferenceUpdate}
                      currentSelections={{
                        preferences: selectedPreference,
                        cut: selectedCut,
                        notes: customNotes,
                      }}
                      disabled={loading}
                      isSubscriptionModal={true}
                    />
                  </div>
                )}

                {/* Product Info */}
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200 mt-3">
                  <div className="space-y-2">
                    {/* Base Price */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Base Price:</span>
                      <span className="text-md font-semibold text-gray-700">
                        ${product?.product_price || product?.price}
                      </span>
                    </div>

                    {/* Preference Total (only show if > 0) */}
                    {preferenceTotal > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">
                          Preferences:
                        </span>
                        <span className="text-md font-semibold text-gray-700">
                          +${preferenceTotal.toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* Divider */}
                    <div className="border-t border-gray-300 my-2"></div>

                    {/* Total Price */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">
                        Price per delivery:
                      </span>
                      <span className="text-xl font-bold text-primary-100">
                        ${totalPricePerDelivery}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Button - Fixed at bottom */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-center flex-shrink-0">
              <button
                onClick={handleSubscribe}
                disabled={loading}
                className="w-4/6 px-4 py-3 text-lg letter-spacing-1 bg-gradient2 text-white rounded-full font-semibold  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (isUpdateMode ? "Updating..." : "Subscribing...") : (isUpdateMode ? "Update Subscription" : "Subscribe")}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default SubscriptionModal;
